import React from "react";
import { useIntl, defineMessages } from "react-intl";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Warning,
  FolderOutlined,
  CloudDownload,
  ImageOutlined,
  PictureAsPdfOutlined,
  OndemandVideoOutlined,
  CloudOff,
} from "@mui/icons-material";
import { Player } from "../../../files/components/Player";

import { useSnackbarConsumer } from "shared/providers/Snackbar";
import { createLog, getPresignedURLByFileId } from "modules/files/services";
import { useAuthenticationConsumer } from "shared/providers/Authentication";
import PDFViewer from "../../../files/components/PDFViewer";
import ImagePreview from "../../../files/components/ImagePreview";
import { toMP4 } from "modules/files/components/QueuedDownloads/services/mp4";

const messages = defineMessages({
  titleCreate: { id: "files.create" },
  titleEdit: { id: "files.edit" },
  create: { id: "create" },
  save: { id: "save" },
  required: { id: "required" },
  success: { id: "files.upload.success" },
  error: { id: "files.upload.error" },
  cancel: { id: "cancel" },
  send: { id: "send" },
  downloadError: { id: "files.download.error" },
  downloadSuccess: { id: "files.download.success" },
  scheduleFile: { id: "files.schedule.file" },
  reachDailyLimit: { id: "files.scheduled.reach.daily.limit" },
  scheduledDownload: { id: "files.scheduled.downloads" },
  convertingDownload: { id: "files.scheduled.converting" },
});

export default function FileList({ rows, path, onClick }) {
  const { readOnly } = useAuthenticationConsumer();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [totalItens, setTotalItens] = React.useState<number>(0);
  const [totalSize, setTotalSize] = React.useState<string>("0");
  const { openSnackbar } = useSnackbarConsumer();
  const [pdfURL, setPdfURL] = React.useState<string>("");
  const [imgURL, setImgURL] = React.useState<string>("");
  const [loadingVideo, setLoadingVideo] = React.useState<boolean>(false);
  const [loadingFile, setLoadingFile] = React.useState<boolean>(false);
  const [videoOpen, setVideoOpen] = React.useState<boolean>(false);
  const [videoName, setVideoName] = React.useState<string>("");
  const [videoURL, setVideoURL] = React.useState<string>("");

  function formatBytes(bytes, decimals = 2): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDownload = async (url: string) => {
    setLoading(true);
    window.open(url);
    setLoading(false);
  };

  const resetVideoOpen = () => {
    setVideoURL("");
    setVideoName("");
    setVideoOpen(false);
    setLoading(false);
  };

  const handlePDFOpen = React.useCallback(async (url: string, fileId = 0) => {
    await createLog({ file_id: fileId, log_type: "PDF_OPEN" });
    scrollTop();
    setLoadingVideo(true);
    setPdfURL(url);
    setLoadingVideo(false);
  }, []);

  const handleImgOpen = React.useCallback(async (url: string, fileId = 0) => {
    await createLog({ file_id: fileId, log_type: "IMAGE_OPEN" });
    scrollTop();
    setLoadingVideo(true);
    setImgURL(url);
    setLoadingVideo(false);
  }, []);

  const handleVideoOpen = async (
    id: number,
    fileName: string,
    download = false
  ) => {
    setLoadingVideo(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    const { data: dataURL } = await getPresignedURLByFileId(id);

    const originalURL = decodeURIComponent(dataURL.url);
    let url = decodeURIComponent(
      dataURL.url.replace(/(.*\.(h264|h265|dav)).*/, "$1")
    );
    if (fileName && url) {
      if (url.toLowerCase().includes(".mp4")) {
        setVideoURL(originalURL);
        setVideoName(fileName);
        setVideoOpen(true);
        setLoadingVideo(false);
        return false;
      }

      setLoadingVideo(true);

      if (download) {
        openSnackbar({
          message: "Iniciando download...",
          severity: "info",
        });
        setTimeout(() => {
          window.open(originalURL);
        }, 1000);
        setLoadingVideo(false);
        return false;
      }

      const { status, data } = await toMP4([url]);

      if (status === 201 && data.bufferUrls[0]) {
        setVideoURL(data.bufferUrls[0]);
        setVideoName(fileName);
        setVideoOpen(true);
      } else {
        openSnackbar({
          message: "Não foi possível realizar a operação.",
          severity: "warning",
        });
      }
    }
    setLoadingVideo(false);
  };

  React.useEffect(() => {
    console.log(rows);
  }, [rows]);

  React.useEffect(() => {
    let totalI = 0;
    let totalS = 0;

    rows.forEach((row) => {
      totalI++;
      totalS += row.size || 0;
    });

    setTotalItens(totalI);
    setTotalSize(formatBytes(totalS));
  }, [rows]);

  return (
    <div>
      <TableContainer component={Paper}>
        <Table size="medium" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Quantidade</TableCell>
              <TableCell align="right">Tamanho</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              style={{ background: "#EEE" }}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              ></TableCell>
              <TableCell align="right">{totalItens} itens</TableCell>
              <TableCell align="right">{totalSize}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>

            {path && (
              <TableRow
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => onClick(path, "prev")}
                >
                  <Tooltip title="folder">
                    <>
                      <FolderOutlined />
                      &#8626;&nbsp;voltar
                    </>
                  </Tooltip>
                </TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            )}

            {rows &&
              rows.map((row, index) => (
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  key={index}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => onClick(row.path, "next")}
                  >
                    <Tooltip title={row.type === "folder" ? "Acessar" : ""}>
                      <>
                        {row.type === "folder" && <FolderOutlined />}
                        &nbsp;{row.path}
                      </>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {row.type === "folder" && row.qty_files > 0
                      ? `${row.qty_files} itens`
                      : ""}
                  </TableCell>
                  <TableCell align="right">
                    {row.type !== "folder" ? formatBytes(row.size || 0) : ""}
                  </TableCell>
                  <TableCell align="right">
                    {!readOnly &&
                      row.type !== "folder" &&
                      row.storageClass !== "STANDARD" && (
                        <IconButton
                          color="primary"
                          size="small"
                          aria-label="Arquivo Congelado - Entrar em contato para download"
                          onClick={() => {}}
                        >
                          <Tooltip title="Arquivo Congelado - Entrar em contato para download">
                            <CloudOff color="error" />
                          </Tooltip>
                        </IconButton>
                      )}
                    {!readOnly &&
                      row.type !== "folder" &&
                      row.storageClass === "STANDARD" && (
                        <>
                          <IconButton
                            color="primary"
                            size="small"
                            aria-label="Download"
                            onClick={() => handleDownload(row.signedURL)}
                          >
                            <Tooltip
                              title={formatMessage(messages.scheduleFile)}
                            >
                              <CloudDownload />
                            </Tooltip>
                          </IconButton>

                          {row?.originalFile?.fileType?.is_video ? (
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label={"Assistir agora"}
                              onClick={() =>
                                handleVideoOpen(
                                  row.originalFile.id,
                                  row.originalFile.name
                                )
                              }
                            >
                              <Tooltip title={"Assistir agora"}>
                                <OndemandVideoOutlined />
                              </Tooltip>
                            </IconButton>
                          ) : (
                            <></>
                          )}
                          {["pdf"].includes(
                            row?.extension.toLocaleLowerCase()
                          ) ? (
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label={"Visualizar"}
                              onClick={() =>
                                handlePDFOpen(
                                  row.signedURL,
                                  row.originalFile.id
                                )
                              }
                            >
                              <Tooltip title={"Visualizar"}>
                                <PictureAsPdfOutlined />
                              </Tooltip>
                            </IconButton>
                          ) : (
                            <></>
                          )}
                          {["jpg", "jpeg", "png", "gif"].includes(
                            row?.extension.toLocaleLowerCase()
                          ) ? (
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label={"Visualizar"}
                              onClick={() =>
                                handleImgOpen(
                                  row.signedURL,
                                  row.originalFile.id
                                )
                              }
                            >
                              <Tooltip title={"Visualizar"}>
                                <ImageOutlined />
                              </Tooltip>
                            </IconButton>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            {!rows && (
              <TableRow
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                style={{ alignItems: "center" }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Warning style={{ width: "20px" }} />
                  &nbsp;&nbsp;Nenhum registro encontrado
                </TableCell>
              </TableRow>
            )}
            <TableRow
              style={{ background: "#EEE" }}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              ></TableCell>
              <TableCell align="right">{totalItens} itens</TableCell>
              <TableCell align="right">{totalSize}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Player
        open={videoOpen}
        videoName={videoName}
        videoURL={videoURL}
        onClose={() => resetVideoOpen()}
      />

      {loadingVideo && (
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFF",
            fontSize: "30px",
            fontWeight: "bold",
            gap: 30,
          }}
        >
          <div>
            <CircularProgress size={80} />
          </div>
          <div>Solicitando, aguarde...</div>
        </div>
      )}

      {pdfURL && pdfURL.length > 0 && (
        <PDFViewer url={pdfURL} onClose={() => setPdfURL("")} />
      )}
      {imgURL && imgURL.length > 0 && (
        <ImagePreview url={imgURL} onClose={() => setImgURL("")} />
      )}
    </div>
  );
}
